import { useCallback, useEffect, useRef, useState } from 'react'

import { stockTakingService, type ShelfItem } from '@services/operator/stockTakingService'

const DEFAULT_PAGE_SIZE = 500

interface UseShelfItemsLoaderArgs {
  stockTakingId: string | null | undefined
  shelfId: string | null | undefined
  enabled: boolean
  pageSize?: number
}

interface UseShelfItemsLoaderResult {
  items: ShelfItem[]
  loading: boolean
  loadedCount: number
  totalExpected: number | null
  error: string | null
  retry: () => void
  reload: () => void
}

/**
 * Static-mode shelf 大資料量分批載入：連續呼叫 getShelfItems 直到 next_cursor === null。
 *
 * 失敗策略：保留已 load 的批次，露 error + retry()，retry 從 lastCursorRef 繼續，
 * 不重 load 已成功的頁（10K 筆 load 一半斷網時不會白做）。
 *
 * 期間 caller 應 disable 所有寫入操作，避免 partial state 下被 submit。
 */
export function useShelfItemsLoader({
  stockTakingId,
  shelfId,
  enabled,
  pageSize = DEFAULT_PAGE_SIZE,
}: UseShelfItemsLoaderArgs): UseShelfItemsLoaderResult {
  const [items, setItems] = useState<ShelfItem[]>([])
  const [loading, setLoading] = useState(false)
  const [totalExpected, setTotalExpected] = useState<number | null>(null)
  const [error, setError] = useState<string | null>(null)

  // 用 ref 追蹤 cursor，避免每批 setState 觸發 effect 重跑造成併發 fetch。
  const lastCursorRef = useRef<string | null>(null)
  const fetchingRef = useRef(false)
  // 用來區別不同的 (stockTakingId, shelfId) session，舊 session 的 in-flight response
  // 不能寫進新 session 的 state。
  const sessionRef = useRef(0)

  const runLoader = useCallback(async () => {
    if (!enabled || !stockTakingId || !shelfId) return
    if (fetchingRef.current) return

    fetchingRef.current = true
    setLoading(true)
    setError(null)
    const session = sessionRef.current

    try {
      while (true) {
        const res = await stockTakingService.getShelfItems(stockTakingId, shelfId, {
          cursor: lastCursorRef.current,
          pageSize,
        })

        // 此期間 (stockTakingId, shelfId) 已換過 → 丟棄這批
        if (session !== sessionRef.current) return

        if (!res.success) {
          setError(res.message || 'Failed to load shelf items')
          return
        }

        const { items: batch, next_cursor, total } = res.data

        if (lastCursorRef.current === null && typeof total === 'number') {
          setTotalExpected(total)
        }

        if (batch.length > 0) {
          setItems((prev) => prev.concat(batch))
        }

        if (next_cursor === null) {
          // 全部載完，cursor 不再前進
          return
        }

        lastCursorRef.current = next_cursor
      }
    } catch (err) {
      if (session !== sessionRef.current) return
      console.error('[useShelfItemsLoader] fetch error:', err)
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      if (session === sessionRef.current) {
        setLoading(false)
      }
      fetchingRef.current = false
    }
  }, [enabled, stockTakingId, shelfId, pageSize])

  // 進入 / 切換 shelf 時重置並啟動
  useEffect(() => {
    sessionRef.current += 1
    lastCursorRef.current = null
    setItems([])
    setTotalExpected(null)
    setError(null)
    if (enabled && stockTakingId && shelfId) {
      void runLoader()
    } else {
      setLoading(false)
    }
    // runLoader 已 useCallback by (enabled, stockTakingId, shelfId, pageSize)
  }, [enabled, stockTakingId, shelfId, pageSize, runLoader])

  const retry = useCallback(() => {
    // 從 lastCursorRef 繼續，不清空 items
    setError(null)
    void runLoader()
  }, [runLoader])

  const reload = useCallback(() => {
    sessionRef.current += 1
    lastCursorRef.current = null
    setItems([])
    setTotalExpected(null)
    setError(null)
    void runLoader()
  }, [runLoader])

  return {
    items,
    loading,
    loadedCount: items.length,
    totalExpected,
    error,
    retry,
    reload,
  }
}
